import { cn, copyToClipboard } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { MacosAction } from '@sohanscript/ui/macos-action';
import { WithTooltip } from '@sohanscript/ui/tooltip';
import type { HTMLAttributes } from 'react';
import { toast } from 'sonner';

export interface SafariProps extends HTMLAttributes<HTMLDivElement> {
	url?: string;
	mini?: boolean;
}
export function Safari({
	url,
	className,
	style,
	mini,
	children,
	...props
}: SafariProps) {
	return (
		<div
			className={cn(
				'corner-bevel flex aspect-safari w-full flex-col overflow-clip rounded-lg border bg-background',
				className,
			)}
			style={{
				...style,
			}}
			{...props}
		>
			<SafariNav mini={mini} url={url} />
			{children}
		</div>
	);
}

function SafariNav({
	url = 'sohanscript.web.app',
	mini,
}: {
	url?: string;
	mini?: boolean;
}) {
	return (
		<div className="flex items-center gap-3 bg-primary/10 px-2 py-2 text-secondary lg:px-4">
			{!mini && (
				<section className="flex w-1/6 shrink-0 items-center gap-2 *:shrink-0 max-md:hidden">
					<MacosAction className="mb-0 origin-left scale-80 opacity-70" />

					<Iconify icon="iconoir:window-tabs" />
					<Iconify icon="lucide:chevron-left" />
					<Iconify icon="lucide:chevron-right" />
				</section>
			)}

			<section
				className={cn(
					'mx-auto flex w-full items-center gap-2 sm:w-5/6 md:w-3/6',
					mini && 'md:w-full',
				)}
			>
				<Iconify icon="mdi:shield-half-full" />
				<div className="flex h-6 min-w-0 flex-1 items-center gap-1 rounded-md bg-primary/10 px-2">
					<Iconify icon="lucide:lock" className="size-3.5" />
					<button
						type="button"
						className="min-w-0 flex-1 cursor-copy truncate text-xs mix-blend-luminosity"
						style={{ color: 'var(--secondary)' }}
						onClick={() => {
							copyToClipboard(url);
							toast.success(m.safari_url_copied());
						}}
					>
						{url}
					</button>
					<WithTooltip className="size-3.5" content={m.safari_reload()}>
						<Iconify
							role="button"
							onClick={() => {
								window.location.reload();
							}}
							icon="lucide:rotate-ccw"
						/>
					</WithTooltip>
				</div>
			</section>
			{!mini && (
				<section className="flex w-1/6 items-center justify-end gap-2 max-md:hidden">
					<WithTooltip content={m.safari_share()}>
						<Iconify
							onClick={async () => {
								if (navigator.share) {
									try {
										await navigator.share({
											title: 'My Page Title',
											text: 'Check this out!',
											url: 'https://example.com',
										});
									} catch {
										toast.error(m.safari_share_error());
									}
								} else {
									toast.warning(m.safari_share_unsupported());
								}
							}}
							role="button"
							icon="lucide:share"
							className="size-3.5"
						/>
					</WithTooltip>
					<WithTooltip content={m.safari_open()}>
						<Iconify
							onClick={() => {
								window.open(url, '_blank', 'noopener,noreferrer');
							}}
							role="button"
							icon="lucide:plus"
							className="size-3.5"
						/>
					</WithTooltip>
					<WithTooltip content={m.safari_copy_url()}>
						<Iconify
							role="button"
							icon="lucide:copy"
							onClick={() => {
								copyToClipboard(url);
								toast.success(m.safari_url_copied());
							}}
							className="size-3.5"
						/>
					</WithTooltip>
				</section>
			)}
		</div>
	);
}
